import type { MantineColorScheme } from '@mantine/core';
import { ActionIcon, Combobox, Text, useCombobox, useMantineColorScheme } from '@mantine/core';
import { IconMoonFilled, IconPercentage50, IconSunFilled } from '@tabler/icons-react';

const ICON_SIZE = 20;

const COLOR_SCHEME_OPTIONS: { value: MantineColorScheme; label: string; icon: React.ReactNode }[] = [
  {
    value: 'light',
    label: 'Light',
    icon: (
      <IconSunFilled
        size={ICON_SIZE}
        className="text-yellow-500"
      />
    ),
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: (
      <IconMoonFilled
        size={ICON_SIZE}
        className="text-gray-400"
      />
    ),
  },
  {
    value: 'auto',
    label: 'System',
    icon: (
      <IconPercentage50
        size={ICON_SIZE}
        className="text-gray-400"
      />
    ),
  },
] as const;

const COLOR_SCHEME_TO_ICON = Object.fromEntries(COLOR_SCHEME_OPTIONS.map(({ value, icon }) => [value, icon] as const));

export default function DarkModeSwitch() {
  const { colorScheme: mantineColorScheme, setColorScheme } = useMantineColorScheme();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = COLOR_SCHEME_OPTIONS.map(({ icon, value, label }) => (
    <Combobox.Option
      value={value}
      key={value}
      active={value === mantineColorScheme}
      selected={value === mantineColorScheme}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          {label}
        </div>
        {value !== mantineColorScheme &&
          value !== 'auto' &&
          // Last condition is so that if we're auto mode, we only show the hotkey next to light mode
          !(value === 'dark' && mantineColorScheme === 'auto') && (
            <Text
              size="xs"
              c="dimmed"
            >
              ⌘+SHIFT+L
            </Text>
          )}
      </div>
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      width={200}
      position="bottom-end"
      onOptionSubmit={(val) => {
        setColorScheme(val as MantineColorScheme);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <ActionIcon
          variant="transparent"
          onClick={() => combobox.toggleDropdown()}
        >
          {COLOR_SCHEME_TO_ICON[mantineColorScheme]}
        </ActionIcon>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
